// soccer_admin/src/pages/ErrorLogs.jsx
import React, { useEffect, useState } from 'react';
import { 
  BugAntIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon,
  InformationCircleIcon, MagnifyingGlassIcon, ChevronLeftIcon,
  ChevronRightIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon
} from '@heroicons/react/24/outline';
import { fetchErrorLogs, resolveError } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const severityColors = {
  error: 'bg-red-600/20 text-red-400',
  warning: 'bg-yellow-600/20 text-yellow-400',
  info: 'bg-blue-600/20 text-blue-400',
  debug: 'bg-gray-600/20 text-gray-400'
};

const severityIcons = {
  error: <ExclamationTriangleIcon className="h-4 w-4" />,
  warning: <ExclamationTriangleIcon className="h-4 w-4" />,
  info: <InformationCircleIcon className="h-4 w-4" />,
  debug: <BugAntIcon className="h-4 w-4" />
};

const ErrorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [filters, setFilters] = useState({ severity: '', resolved: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = async (currentPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchErrorLogs(currentPage, limit, filters);
      const logsData = res.data.data || res.data;
      setLogs(logsData);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
        setTotalLogs(res.data.pagination.total || 0);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load error logs. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(page);
  }, [page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ severity: '', resolved: '' });
    setSearchTerm('');
    setPage(1);
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // ✅ تحسين دالة handleResolve
  const handleResolve = async (logId) => {
    if (!window.confirm('Mark this error as resolved?')) return;
    
    // منع النقر المتكرر
    if (resolvingId === logId) return;
    setResolvingId(logId);

    try {
      const response = await resolveError(logId);
      
      // التحقق من نجاح العملية من الاستجابة
      if (response && response.success) {
        // تحديث الحالة المحلية فوراً (تحسين تجربة المستخدم)
        setLogs(prevLogs => 
          prevLogs.map(log => 
            log.id === logId 
              ? { ...log, resolved: true, resolved_at: new Date().toISOString() }
              : log
          )
        );
        
        // إعادة تحميل البيانات للتأكد من التزامن مع قاعدة البيانات
        await loadLogs(page);
      } else {
        // إذا لم تكن الاستجابة ناجحة، عرض رسالة الخطأ
        alert(response?.error || 'Failed to resolve error. Please try again.');
        // إعادة تحميل البيانات لإظهار الحالة الحقيقية
        await loadLogs(page);
      }
    } catch (err) {
      console.error('Error resolving:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to resolve error';
      alert(errorMessage);
      // إعادة تحميل البيانات لإظهار الحالة الحقيقية
      await loadLogs(page);
    } finally {
      setResolvingId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.error_message?.toLowerCase().includes(term) ||
      log.username?.toLowerCase().includes(term) ||
      log.stack_trace?.toLowerCase().includes(term) ||
      log.context?.toString().toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-white text-lg">Loading error logs...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BugAntIcon className="h-8 w-8 text-red-400" />
              Error Logs
            </h1>
            <p className="text-gray-400 text-sm mt-1">Monitor and manage application errors</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadLogs(page)}
              disabled={loading}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div>
          <label className="block text-gray-400 text-sm mb-1">Severity</label>
          <select
            name="severity"
            value={filters.severity}
            onChange={handleFilterChange}
            className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
          >
            <option value="">All</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>
        <div>
          <label className="block text-gray-400 text-sm mb-1">Status</label>
          <select
            name="resolved"
            value={filters.resolved}
            onChange={handleFilterChange}
            className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
          >
            <option value="">All</option>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-gray-400 text-sm mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by message, user, stack trace..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-yellow-500"
            />
          </div>
        </div>
        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="w-full bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-lg transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error List */}
      {error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center text-red-400">
          <p>{error}</p>
          <button onClick={() => loadLogs(page)} className="mt-4 bg-red-500/20 hover:bg-red-500/30 px-4 py-2 rounded-lg text-sm">
            Retry
          </button>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <p className="text-lg">No errors found</p>
          <p className="text-sm">Everything is running smoothly! 🎉</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              className={`bg-gray-800 rounded-xl border transition-all ${
                log.resolved ? 'border-green-500/30 opacity-70' : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${severityColors[log.severity] || severityColors.info}`}>
                        {severityIcons[log.severity] || severityIcons.info}
                        {log.severity || 'info'}
                      </span>
                      {log.resolved ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs bg-green-600/20 px-2 py-0.5 rounded-full">
                          <CheckCircleIcon className="h-3 w-3" /> Resolved
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs bg-red-600/20 px-2 py-0.5 rounded-full">
                          <ExclamationTriangleIcon className="h-3 w-3" /> Unresolved
                        </span>
                      )}
                      {log.user_id && (
                        <span className="text-xs text-gray-400">
                          User: <span className="text-white">{log.username || `#${log.user_id}`}</span>
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="mt-2">
                      <p className="text-white font-medium break-words">{log.error_message}</p>
                      {log.context && (
                        <button
                          onClick={() => toggleExpand(log.id)}
                          className="text-xs text-blue-400 hover:text-blue-300 transition mt-1"
                        >
                          {expandedId === log.id ? 'Hide context' : 'Show context'}
                        </button>
                      )}
                      {expandedId === log.id && log.context && (
                        <pre className="mt-2 bg-gray-900/80 p-3 rounded-lg text-xs text-gray-300 overflow-x-auto max-h-40">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      )}
                      {log.stack_trace && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">Stack Trace</summary>
                          <pre className="mt-1 bg-gray-900/80 p-3 rounded-lg text-xs text-gray-300 overflow-x-auto max-h-40 whitespace-pre-wrap">
                            {log.stack_trace}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {!log.resolved && (
                      <button
                        onClick={() => handleResolve(log.id)}
                        disabled={resolvingId === log.id}
                        className="text-green-400 hover:text-green-300 transition p-1 disabled:opacity-50"
                        title="Mark as resolved"
                      >
                        {resolvingId === log.id ? (
                          <ArrowPathIcon className="h-5 w-5 animate-spin" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => toggleExpand(log.id)}
                      className="text-gray-400 hover:text-white transition p-1"
                      title={expandedId === log.id ? 'Collapse' : 'Expand'}
                    >
                      {expandedId === log.id ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-3">
                  {log.url && <span>📍 {log.url}</span>}
                  {log.source && <span>📄 {log.source}:{log.line || '?'}</span>}
                  {log.ip_address && <span>🌐 {log.ip_address}</span>}
                  <span>🆔 #{log.id}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6 text-white">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} ({totalLogs} errors)
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ErrorLogs;